import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import isEmpty from 'lodash/isEmpty';
import { Combobox } from '@shared/fields';
import RadioGroup from '@shared/fields/RadioGroup';
import media from '../../utils/mediaQueries';

import Btn from '../buttons/Btn';
import Field from '../fields/Field';
import Textarea from '../fields/Textareafield';
import Loader from '../loader';
import { Success } from '../messages/Success';

import stores from '../../stores';
import { MAIN_BLUE } from '../../theme';

const options = [
  {
    text: 'Oui',
    value: 'true',
  },
  {
    text: 'Non',
    value: 'false',
  },
];

const ReportSchema = Yup.object().shape({
  name: Yup.string()
    .matches(
      /^[A-Za-z\u00C0-\u00FF\-\ ]+$/,
      'Ce champ ne peut contenir que des lettres, des espaces ou un tiret (-).',
    )
    .required('Ce champ est obligatoire.'),
  email: Yup.string()
    .trim('Ce champ ne peut pas inclure d\'espace au début et à la fin')
    .strict(true)
    .email('Merci de renseigner une adresse email valide.')
    .required('Ce champ est obligatoire.'),
  reason: Yup.string().required('Ce champ est obligatoire.'),
});

const Overlay = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 999999999;
`;

const Modal = styled.div`
  position: relative;
  border-radius: 12px;
  box-shadow: 0 -1px 1px 0 rgba(0, 0, 0, 0.02),
    0 2px 4px 0 rgba(155, 132, 135, 0.06), 0 4px 12px 0 rgba(119, 94, 98, 0.05);
  background-color: #ffffff;
  width: 400px;
  overflow: hidden !important;
  ${media.mobile`
    width: 100%;
    margin: 10px;
  `}
`;

const Content = styled.div`
  overflow-y: auto;
  padding: 20px 24px 24px;
  height: calc(100vh - 30px);
  max-height: 758px;
  ${media.mobile`
    height: calc(100vh - 30px);
  `}
`;

const CloseButton = styled.button`
  width: 16px;
  height: 16px;
  position: absolute;
  top: 8px;
  right: 15px;
  border: none;
  background: none;
  padding: 0;
  outline: none;
  ${media.mobile`
    right: 5px;
  `}
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 800;
  line-height: 1.44;
  letter-spacing: 0.2px;
  color: #354354;
`;

const Text = styled.div`
  font-size: 16px;
  color: #354354;
  margin-bottom: 16px;
`;

const Link = styled.a`
  font-size: 16px;
  line-height: 1.33;
  letter-spacing: 0.2px;
  color: ${({ isAvisSolution }) => isAvisSolution ? '#313b82' : MAIN_BLUE};
  text-decoration: none;
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: ${({ isAvisSolution }) => isAvisSolution ? '#313b82' : MAIN_BLUE};
  }
`;

class ReportClassComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedOption: options[0].value,
    };
  }

  componentDidMount = () => {
    const { isSchool } = this.props;
    stores.reviewsStore.getReasons(isSchool);
  };

  onOptionChange = (option) => {
    this.setState({
      selectedOption: option,
    });
  };

  onFormSubmit = (values, formikBag) => {
    const { reviewId, isSchool, isAvisSolution } = this.props;
    const { selectedOption } = this.state;
    const data = {
      author_name: values.name,
      author_email: values.email,
      is_employee: JSON.parse(selectedOption),
      description: values.description,
    };

    if (isSchool) {
      data.rating_id = reviewId;
      data.review_report_reason_id = values.reason;
      if (isAvisSolution) {
        data.partner = 'avis-solution';
      }
    } else {
      data.exam_center_review_id = reviewId;
      data.exam_center_review_report_reason_id = values.reason;
    }

    stores.reviewsStore.report(data, formikBag, isSchool);
  };

  render() {
    const { onClose, isSchool, isAvisSolution } = this.props;
    const { selectedOption } = this.state;
    const {
      reportReasons,
      isLoading,
      isSending,
      reportResult,
    } = stores.reviewsStore;

    return (
      <Overlay>
        <Modal>
          {isEmpty(reportReasons) ? (
            isLoading && <Loader containerHeight={400} />
          ) : (
            <>
              <CloseButton onClick={onClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                >
                  <path
                    fill="#CCD0D4"
                    d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm2.924 5.062a.545.545 0 0 0-.771 0l-2.07 2.153-2.25-2.182a.545.545 0 1 0-.757.785l2.237 2.153-2.237 2.153a.545.545 0 1 0 .757.785l2.25-2.182 2.07 2.153a.545.545 0 1 0 .785-.756L8.855 7.942l2.083-2.182a.545.545 0 0 0-.014-.698z"
                  />
                </svg>
              </CloseButton>
              <Content>
                {reportResult === true ? (
                  <Success message="Votre signalement a été transmis." />
                ) : (
                  <Formik
                    initialValues={{
                      name: '',
                      email: '',
                      reasonId: '',
                      description: '',
                    }}
                    validationSchema={ReportSchema}
                    onSubmit={this.onFormSubmit}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                    }) => (
                      <Form>
                        <Title>Signaler un avis</Title>
                        <section>
                          <Text>
                            Merci de renseigner les informations suivantes
                          </Text>
                          <Field
                            type="text"
                            placeholder="Votre nom"
                            name="name"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.name}
                            valid={touched.name && !errors.name}
                            errorMessage={
                              errors.name && touched.name ? errors.name : null
                            }
                          />
                          <Field
                            type="text"
                            placeholder="Votre email"
                            name="email"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.email}
                            valid={touched.email && !errors.email}
                            errorMessage={
                              errors.email && touched.email
                                ? errors.email
                                : null
                            }
                          />
                        </section>
                        <section>
                          <Text>
                            Travaillez-vous pour
                            {' '}
                            {isSchool ? 'l’auto-école' : 'le centre d\'examen'}
                            {' '}
                            concernée par cet
                            avis
                          </Text>
                          <RadioGroup
                            options={options}
                            small
                            currentOption={selectedOption}
                            onOptionChange={this.onOptionChange}
                            activeBackgroundColor={isAvisSolution ? '#313b82' : MAIN_BLUE}
                          />
                        </section>
                        <section>
                          <Text>
                            Pour être supprimé, l’avis doit être contraire aux &nbsp;
                            <Link href={isAvisSolution ? '/a-propos/conditions-particulieres-avis?partner=avis-solution' : '/legal#avis'} target="_blank" rel="noopener noreferrer" isAvisSolution={isAvisSolution}>
                              Conditions générales d’Utilisation
                            </Link>
                            . Merci de consulter ces dernières et de préciser
                            quel critère de publication vous semble avoir été
                            enfreint.
                          </Text>
                          <Combobox
                            placeholder="Motif du signalement"
                            name="reason"
                            options={reportReasons}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.reason}
                            valid={touched.reason && !errors.reason}
                            errorMessage={
                              errors.reason && touched.reason
                                ? errors.reason
                                : null
                            }
                          />
                          <Textarea
                            placeholder="Précisez le (ou les) passage(s) concernés"
                            name="description"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.description}
                          />
                        </section>
                        <Btn small isLoading={isSending} text="Envoyer" color={isAvisSolution ? '#313b82' : MAIN_BLUE} />
                      </Form>
                    )}
                  </Formik>
                )}
              </Content>
            </>
          )}
        </Modal>
      </Overlay>
    );
  }
}

ReportClassComponent.propTypes = {
  reviewId: PropTypes.string,
  onClose: PropTypes.func,
  isSchool: PropTypes.bool,
  isAvisSolution: PropTypes.bool,
};

ReportClassComponent.defaultProps = {
  reviewId: '',
  onClose: () => {},
  isSchool: true,
  isAvisSolution: false,
};

const Report = observer(ReportClassComponent);

export default Report;
